import React, { useState, useEffect, ReactNode } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';
import { RangeDTO, RangeStatusDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useProvinces } from '../../hooks/useProvinceApi';
import { useRangeStatuses } from '../../hooks/useRangeStatusApi';
import { useRangeTypes } from '../../hooks/useRangeTypeApi';
import { useRangeShootingReasons } from '../../hooks/useRangeShootingReasonApi';
import { useFileApi } from '../../hooks/useFileApi';
import MapboxAutocomplete from '../common/formElements/MapboxAutocomplete';
import MapboxMap from '../common/MapboxMap';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface RangeFormProps {
  initialValues: RangeDTO;
  onSubmit: (data: RangeDTO) => void;
  onCancel: () => void;
  isPublicView?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  location: {
    id: 'location',
    title: 'Location',
  },
  applicantInfo: {
    id: 'applicant-info',
    title: 'Personal details of applicant',
  },
  witnessInfo: {
    id: 'witness-info',
    title: 'Personal details of witness',
  },
  images: {
    id: 'images',
    title: 'Range Images',
  },
  additionalInfo: {
    id: 'additional-info',
    title: 'Additional Info',
  },
  admin: {
    id: 'admin',
    title: 'Admin',
  },
  feedback: {
    id: 'feedback',
    title: 'Feedback',
  },
} as const;

const RangeForm: React.FC<RangeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isPublicView = false,
}) => {
  const [formData, setFormData] = useState<RangeDTO>(initialValues);

  const statusFieldName = 'rangeStatusId'

  const [statusOptions, setStatusOptions] = useState<
    { value: string; label: ReactNode }[]
  >([]); // Changed this line
  const [typeOptions, setTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [shootingReasonOptions, setShootingReasonOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { uploadFile, getFileUrl } = useFileApi();

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: rangeTypesData } = useRangeTypes();
  const { data: rangeStatusesData } = useRangeStatuses();
  const { data: rangeShootingReasonsData } = useRangeShootingReasons();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setTypeOptions(
      rangeTypesData?.map((t) => ({
        value: t.id as string,
        label: t.title || '',
      })) || [],
    );
  }, [rangeTypesData]);

  useEffect(() => {
    setStatusOptions(
      (rangeStatusesData ?? []).map((s: RangeStatusDTO) => ({
        value: s.id as string,
        label: (
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full inline-block"
              style={{ backgroundColor: s.colourGrading?.colour }}
            />
            <span>{s.title}</span>
          </div>
        ),
      })),
    );
  }, [rangeStatusesData]);

  useEffect(() => {
    setShootingReasonOptions(
      rangeShootingReasonsData?.map((r) => ({
        value: r.id as string,
        label: r.title || '',
      })) || [],
    );
  }, [rangeShootingReasonsData]);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    closestCity: [{ required: true, message: 'Closest City is required' }],
    physicalAddress: [
      { required: true, message: 'Physical Address is required' },
    ],
    lengthOfRange: [
      { required: true, message: 'Length Of Range is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Length Of Range must be a positive number',
      },
    ],
    applicantFirstName: [
      { required: true, message: 'Applicant First Name is required' },
    ],
    applicantLastName: [
      { required: true, message: 'Applicant Last Name is required' },
    ],
    applicantIdNumber: [
      { required: true, message: 'Applicant Id Number is required' },
    ],
    applicantEmail: [
      { required: true, message: 'Applicant Email is required' },
    ],
    applicantAltEmail: [
      { required: true, message: 'Applicant Email (Alternitive) is required' },
    ],
    applicantPhone: [
      { required: true, message: 'Applicant Phone is required' },
    ],
    applicantAltPhone: [
      { required: true, message: 'Applicant Phone (Alternitive) is required' },
    ],
    rangeTypeId: [{ required: true, message: 'Range Type is required' }],
    rangeStatusId: [{ required: !isPublicView, message: 'Range Status is required' }],
    provinceId: [{ required: true, message: 'Province is required' }],
    shootingPointImageId: [
      { required: true, message: 'Shooting Point Image is required' },
    ],
    backStopImageId: [
      { required: true, message: 'Back Stop Image is required' },
    ],
    rangeShootingReasonOther: [
      {
        custom: (value, formData) =>
          isOtherReason(shootingReasonOptions, formData.rangeShootingReasonId)
            ? !!value
            : true,
        message: 'Other Reason is required when Other is selected',
      },
    ],
  };

  const { errors, validate } = useFormValidation<RangeDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange =
    (field: keyof RangeDTO) =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          try {
            setUploadingFiles((prev) => ({ ...prev, [field]: true }));
            const response = await uploadFile('files', file.name, file, 'ranges');
            if (response && response.success && response.data) {
              setFormData((prev) => ({
                ...prev,
                [field]: response.data,
              }));
            }
          } catch (error) {
            console.error('Error handling file:', error);
          } finally {
            setUploadingFiles((prev) => ({ ...prev, [field]: false }));
          }
        }
      };

  const isOtherReason = (
    options: { value: string; label: string }[],
    selectedId?: string,
  ) => {
    const selectedOption = options.find((opt) => opt.value === selectedId);
    return selectedOption?.label.toLowerCase() === 'other';
  };

  const sections = isPublicView
    ? Object.values(FORM_SECTIONS).filter(s => s.id !== 'admin' && s.id !== 'feedback')
    : Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} hideSectionNav={isPublicView} formTitle="Range Registration">
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="rangeTypeId"
          label="Range Type"
          type="dropdown"
          value={formData.rangeTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Range Type"
          required={true}
          error={errors.rangeTypeId}
          options={typeOptions}
        />
        {!isPublicView && (
          <FormField
            id="rangeStatusId"
            label="Range Status"
            type="dropdown"
            value={formData.rangeStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Range Status"
            required={true}
            error={errors.rangeStatusId}
            options={statusOptions}
          />
        )}
        <FormField
          id="title"
          label="Range Name"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="isFriendly"
          label="Is Friendly"
          type="checkbox"
          value={formData.isFriendly || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Friendly"
          required={false}
          error={errors.isFriendly}
          helper="Check to make range friendly"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.location.id}
        title={FORM_SECTIONS.location.title}
        columns={1}
      >
        <FormField
          id="provinceId"
          label="Province"
          type="dropdown"
          value={formData.provinceId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Province"
          required={true}
          error={errors.provinceId}
          options={(provinces || []).map((p) => ({
            value: p.id ?? '',
            label: p.title ?? '',
          }))}
        />
        <FormField
          id="closestCity"
          label="Closest City/Town"
          type="text"
          value={formData.closestCity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Closest City"
          required={true}
          error={errors.closestCity}
        />
        <MapboxAutocomplete
          fieldId="physicalAddress"
          fieldLabel="Physical Address"
          value={formData.physicalAddress || ''}
          onChange={(e) => handleChange(e, setFormData)}
          onLocationSelect={(latitude, longitude) => {
            setFormData((prev) => ({
              ...prev,
              latitude,
              longitude,
            }));
          }}
          placeholder="Enter Physical Address"
          required={true}
        />
        <MapboxMap
          latitude={formData.latitude}
          longitude={formData.longitude}
          onMarkerDragEnd={(latitude, longitude) => {
            setFormData((prev) => ({
              ...prev,
              latitude,
              longitude,
            }));
          }}
        />
        <FormField
          id="lengthOfRange"
          label="The length of the range (meters)"
          type="number"
          value={formData.lengthOfRange || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Length Of Range"
          required={true}
          error={errors.lengthOfRange}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.applicantInfo.id}
        title={FORM_SECTIONS.applicantInfo.title}
      >
        <FormField
          id="applicantFirstName"
          label="First Name"
          type="text"
          value={formData.applicantFirstName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter First Name"
          required={true}
          error={errors.applicantFirstName}
        />
        <FormField
          id="applicantLastName"
          label="Last Name"
          type="text"
          value={formData.applicantLastName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Applicant Last Name"
          required={true}
          error={errors.applicantLastName}
        />
        <FormField
          id="applicantIdNumber"
          label="Id Number"
          type="text"
          value={formData.applicantIdNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Applicant Id Number"
          required={true}
          error={errors.applicantIdNumber}
        />
        <FormField
          id="applicantEmail"
          label="Email"
          type="text"
          value={formData.applicantEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Applicant Email"
          required={true}
          error={errors.applicantEmail}
        />
        <FormField
          id="applicantAltEmail"
          label="Email (Alternative)"
          type="text"
          value={formData.applicantAltEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email (Alternative)"
          required={true}
          error={errors.applicantAltEmail}
        />
        <FormField
          id="applicantPhone"
          label="Phone"
          type="text"
          value={formData.applicantPhone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Applicant Phone"
          required={true}
          error={errors.applicantPhone}
        />
        <FormField
          id="applicantAltPhone"
          label="Phone (Alternative)"
          type="text"
          value={formData.applicantAltPhone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Phone (Alternative)"
          required={true}
          error={errors.applicantAltPhone}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.witnessInfo.id}
        title={FORM_SECTIONS.witnessInfo.title}
      >
        <FormField
          id="witnessFirstName"
          label="First Name"
          type="text"
          value={formData.witnessFirstName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter First Name"
          required={false}
          error={errors.witnessFirstName}
        />
        <FormField
          id="witnessLastName"
          label="Last Name"
          type="text"
          value={formData.witnessLastName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Witness Last Name"
          required={false}
          error={errors.witnessLastName}
        />
        <FormField
          id="witnessIdNumber"
          label="Id Number"
          type="text"
          value={formData.witnessIdNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Witness Id Number"
          required={false}
          error={errors.witnessIdNumber}
        />
        <FormField
          id="witnessEmail"
          label="Email"
          type="text"
          value={formData.witnessEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Witness Email"
          required={false}
          error={errors.witnessEmail}
        />
        <FormField
          id="witnessAltEmail"
          label="Email (Alternative)"
          type="text"
          value={formData.witnessAltEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email (Alternative)"
          required={false}
          error={errors.witnessAltEmail}
        />
        <FormField
          id="witnessPhone"
          label="Witness Phone"
          type="text"
          value={formData.witnessPhone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Phone"
          required={false}
          error={errors.witnessPhone}
        />
        <FormField
          id="witnessAltPhone"
          label="Phone (Alternative)"
          type="text"
          value={formData.witnessAltPhone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Phone (Alternative)"
          required={false}
          error={errors.witnessAltPhone}
        />
      </FormSection>
      <FormSection
        id={FORM_SECTIONS.images.id}
        title={FORM_SECTIONS.images.title}
      >
        {formData.shootingPointImageId && (
          <ImageLink
            fileId={formData.shootingPointImageId}
            label="Shooting Point Image"
          />
        )}
        <FileUpload
          id="shootingPointImageUpload"
          onChange={handleFileChange('shootingPointImageId')}
          title="Upload Shooting Point Image"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.shootingPointImageId}
          isUploading={uploadingFiles['shootingPointImageId']}
        />

        {formData.backStopImageId && (
          <ImageLink
            fileId={formData.backStopImageId}
            label="Back Stop Image"
          />
        )}
        <FileUpload
          id="backStopImageUpload"
          onChange={handleFileChange('backStopImageId')}
          title="Upload Back Stop Image"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.backStopImageId}
          isUploading={uploadingFiles['backStopImageId']}
        />

        {formData.googleMapsImageId && (
          <ImageLink
            fileId={formData.googleMapsImageId}
            label="Google Maps Image"
          />
        )}
        <FileUpload
          id="googleMapsImageUpload"
          onChange={handleFileChange('googleMapsImageId')}
          title="Upload Google Maps Image"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['googleMapsImageId']}
        />

        {formData.otherImageId && (
          <ImageLink fileId={formData.otherImageId} label="Other Image" />
        )}
        <FileUpload
          id="otherImageUpload"
          onChange={handleFileChange('otherImageId')}
          title="Upload Other Image"
          accept={FileTypeConstants.ALL_SUPPORTED}
          isUploading={uploadingFiles['otherImageId']}
        />
      </FormSection>
      <FormSection
        id={FORM_SECTIONS.additionalInfo.id}
        title={FORM_SECTIONS.additionalInfo.title}
        columns={1}
      >
        <FormField
          id="rangeShootingReasonId"
          label="Please select a reason, why it is absolutely necessary to shoot the NHSA In-House Postal targets or NHSA practice targets on a private range"
          type="dropdown"
          value={formData.rangeShootingReasonId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Shooting Reason"
          required={false}
          error={errors.rangeShootingReasonId}
          options={shootingReasonOptions}
        />
        {isOtherReason(
          shootingReasonOptions,
          formData.rangeShootingReasonId,
        ) && (
            <FormField
              id="rangeShootingReasonOther"
              label="Other Reason"
              type="text"
              value={formData.rangeShootingReasonOther || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Other Reason"
              required={true}
              error={errors.rangeShootingReasonOther}
            />
          )}
      </FormSection>
      {!isPublicView && (
        <>
          <FormSection
            id={FORM_SECTIONS.admin.id}
            title={FORM_SECTIONS.admin.title}
            columns={1}
          >
            <FormField
              id="notes"
              label="Admin Notes"
              type="textarea"
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Notes"
              required={false}
              error={errors.notes}
            />
          </FormSection>
          <FormSection
            id={FORM_SECTIONS.feedback.id}
            title={FORM_SECTIONS.feedback.title}
          >
            <FeedbackForm
              componentId={ComponentConstants.RANGES}
              recordId={formData.id ?? ''}
              initialData={formData}
              componentStatuses={statusOptions}
              statusFieldName={statusFieldName}
              onChange={(updatedData) => {
                setFormData((prev) => ({
                  ...prev,
                  ...updatedData,
                }));
              }}
            />
          </FormSection>
        </>
      )}
    </Form>
  );
};

export default RangeForm;
